import React, { FunctionComponent, useCallback, useMemo, useRef, useState } from 'react'
import { TaskSection } from './TaskSection'
import { TaskEmptyState } from './TaskEmptyState'
import { TaskPageContentProps } from './TaskPageContent.model'
import { TaskInformation, TaskStatus, TaskCategory } from '../../../model/Task'
import { TaskListFilters } from '../TaskListFilters'
import { CdaContext } from '../../../model/CdaContext'
import { AnalysisResultModal } from '../../cda'
import {
  TaskPageContentFiltersContext,
  TaskPageContentFiltersContextType,
  TaskPageContextState,
} from './TaskPageContent.context'
import { addTestIdSuffix } from '../../../misc/testId.utilities'
import { TaskInformationModal } from './TaskInformationModal'
import { TaskTableColumn } from '../TaskTable/TaskTable.model'
import styles from './TaskPageContent.module.scss'
import { useSearchParams } from 'react-router-dom'
import { TASK_CATEGORY_OPTIONS } from '../TaskTable/TaskTableRow/TaskTableRow.model'
import { LightPatient } from '../../../model/Patient'
import { SORT_DIRECTION } from '../../../model/Resource'
import { TaskSorting } from '../../../model/Task'

export const TaskPageContent: FunctionComponent<TaskPageContentProps> = ({
  tasksTodo,
  tasksDone,
  tasksDocument,
  loading,
  onCreateOrUpdate,
  onDeleteTask,
  newTask,
  removeNewTask,
  onDeleteAllDoneTasks,
  testId,
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const searchParamsRef = useRef(searchParams)
  searchParamsRef.current = searchParams

  const filters = useMemo<TaskPageContextState>(
    () => ({
      title: searchParams.get('title') ?? '',
      types: searchParams.get('types')
        ? searchParams
            .get('types')!
            .split(',')
            .map(
              (val) =>
                TASK_CATEGORY_OPTIONS.find((o) => o.value === val) ?? {
                  value: val as TaskCategory,
                  label: val,
                },
            )
        : [],
      patient: searchParams.get('patientId')
        ? {
            value: { id: parseInt(searchParams.get('patientId')!, 10) } as LightPatient,
            label: searchParams.get('patientLabel') ?? '',
          }
        : null,
      startDate: searchParams.get('startDate') ? new Date(searchParams.get('startDate')!) : null,
      endDate: searchParams.get('endDate') ? new Date(searchParams.get('endDate')!) : null,
      order: (searchParams.get('order') as SORT_DIRECTION) ?? SORT_DIRECTION.DESC,
      orderBy: (searchParams.get('orderBy') as TaskSorting) ?? 'date',
      hideAutoCreated: searchParams.get('hideAutoCreated') === 'true',
      assignedToMe: searchParams.get('assignedToMe') === 'true',
    }),
    [searchParams],
  )

  const setFilters = useCallback(
    (partial: Partial<TaskPageContextState>) => {
      const next = new URLSearchParams(searchParamsRef.current)
      if ('title' in partial) {
        partial.title ? next.set('title', partial.title) : next.delete('title')
      }
      if ('types' in partial && partial.types !== undefined) {
        const val = partial.types.map((t) => t.value).join(',')
        val ? next.set('types', val) : next.delete('types')
      }
      if ('patient' in partial) {
        if (partial.patient) {
          next.set('patientId', String(partial.patient.value.id))
          next.set('patientLabel', partial.patient.label)
        } else {
          next.delete('patientId')
          next.delete('patientLabel')
        }
      }
      if ('startDate' in partial) {
        partial.startDate
          ? next.set('startDate', partial.startDate.toISOString())
          : next.delete('startDate')
      }
      if ('endDate' in partial) {
        partial.endDate
          ? next.set('endDate', partial.endDate.toISOString())
          : next.delete('endDate')
      }
      if ('order' in partial && partial.order) {
        next.set('order', partial.order)
      }
      if ('orderBy' in partial && partial.orderBy) {
        next.set('orderBy', partial.orderBy)
      }
      if ('hideAutoCreated' in partial && partial.hideAutoCreated !== undefined) {
        partial.hideAutoCreated
          ? next.set('hideAutoCreated', 'true')
          : next.delete('hideAutoCreated')
      }
      if ('assignedToMe' in partial && partial.assignedToMe !== undefined) {
        partial.assignedToMe ? next.set('assignedToMe', 'true') : next.delete('assignedToMe')
      }
      searchParamsRef.current = next
      setSearchParams(next, { replace: true })
    },
    [setSearchParams],
  )

  const contextValue: TaskPageContentFiltersContextType = useMemo(
    () => ({ filters, setFilters }),
    [filters, setFilters],
  )

  const totalTaskItem = useMemo(
    () => tasksTodo.filter((task) => task.id !== undefined).length,
    [tasksTodo],
  )

  const totalTaskDocumentItem = useMemo(
    () => tasksDocument.filter((task) => task.id !== undefined).length,
    [tasksDocument],
  )

  const totalItemCount = useMemo(
    () => totalTaskItem + tasksDone.length + totalTaskDocumentItem,
    [totalTaskItem, tasksDone.length, totalTaskDocumentItem],
  )

  const tasksDoneFiltered = useMemo(() => tasksDone.filter((task) => !task.document), [tasksDone])
  const tasksDocumentDone = useMemo(() => tasksDone.filter((task) => task.document), [tasksDone])

  const [taskInformation, setTaskInformation] = useState<TaskInformation[] | undefined>()
  const [cdaContext, setCdaContext] = useState<CdaContext>()

  const handleOpenTaskInformation = useCallback((taskInformation: TaskInformation[]) => {
    setTaskInformation(taskInformation)
  }, [])

  const handleOpenCdaInformation = useCallback((cdaContext: CdaContext) => {
    setCdaContext(cdaContext)
  }, [])

  return (
    <TaskPageContentFiltersContext.Provider value={contextValue}>
      <div className={styles.taskPageContainer} data-test-id={addTestIdSuffix('root', testId)}>
        <div className={styles.taskPageFiltersContainer}>
          <TaskListFilters testId={addTestIdSuffix('filters', testId)} />
        </div>

        <>
          <TaskSection
            tasksStatus={TaskStatus.TODO}
            onlyDocumentLinked
            itemCount={totalTaskDocumentItem}
            totalItemCount={totalItemCount}
            tasks={tasksDocument}
            displayColumns={[
              TaskTableColumn.Document,
              TaskTableColumn.Patient,
              TaskTableColumn.DocumentStep,
              TaskTableColumn.AssignedTo,
              TaskTableColumn.CreatedAt,
              TaskTableColumn.Actions,
            ]}
            onCreateOrUpdate={onCreateOrUpdate}
            onDeleteTask={onDeleteTask}
            removeNewTask={removeNewTask}
            openTaskInformation={handleOpenTaskInformation}
            openCdaInformation={handleOpenCdaInformation}
            testId="task-page-section-document"
          />
          <TaskSection
            tasksStatus={TaskStatus.DONE}
            itemCount={tasksDocumentDone.length}
            totalItemCount={totalItemCount}
            onlyDocumentLinked
            displayColumns={[
              TaskTableColumn.Document,
              TaskTableColumn.Patient,
              TaskTableColumn.DocumentStep,
              TaskTableColumn.AssignedTo,
              TaskTableColumn.CreatedAt,
              TaskTableColumn.Actions,
            ]}
            tasks={tasksDocumentDone}
            onCreateOrUpdate={onCreateOrUpdate}
            onDeleteTask={onDeleteTask}
            onDeleteAllDoneTasks={onDeleteAllDoneTasks}
            testId="task-page-section-done"
          />
        </>

        <TaskSection
          tasksStatus={TaskStatus.TODO}
          itemCount={totalTaskItem}
          totalItemCount={totalItemCount}
          tasks={tasksTodo}
          displayColumns={[
            TaskTableColumn.Title,
            TaskTableColumn.CdaContext,
            TaskTableColumn.Category,
            TaskTableColumn.Patient,
            TaskTableColumn.CreatedAt,
            TaskTableColumn.Actions,
          ]}
          onCreateOrUpdate={onCreateOrUpdate}
          onDeleteTask={onDeleteTask}
          removeNewTask={removeNewTask}
          openTaskInformation={handleOpenTaskInformation}
          openCdaInformation={handleOpenCdaInformation}
          testId="task-page-section-todo"
        />
        {tasksTodo.length === 0 &&
          tasksDone.length === 0 &&
          tasksDocument.length === 0 &&
          !filters.assignedToMe &&
          !loading && (
            <div className={styles.taskPageEmptyStateContainer}>
              <TaskEmptyState newTask={newTask} />
            </div>
          )}
        <TaskSection
          tasksStatus={TaskStatus.DONE}
          itemCount={tasksDoneFiltered.length}
          totalItemCount={totalItemCount}
          tasks={tasksDoneFiltered}
          displayColumns={[
            TaskTableColumn.Title,
            TaskTableColumn.CdaContext,
            TaskTableColumn.Category,
            TaskTableColumn.Patient,
            TaskTableColumn.CreatedAt,
            TaskTableColumn.Actions,
          ]}
          onCreateOrUpdate={onCreateOrUpdate}
          onDeleteTask={onDeleteTask}
          onDeleteAllDoneTasks={onDeleteAllDoneTasks}
          testId="task-page-section-done"
        />
        <TaskInformationModal
          display={!!taskInformation}
          taskInformation={taskInformation}
          onClose={() => setTaskInformation(undefined)}
        />
        <AnalysisResultModal
          display={!!cdaContext}
          onClose={() => setCdaContext(undefined)}
          result={cdaContext?.analysisResult ?? []}
        />
      </div>
    </TaskPageContentFiltersContext.Provider>
  )
}
